import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type {
  IGameState,
  IPlayer,
  IQueueEvent,
  TContributingEvents,
} from "../../types";
import { FilterKey } from "./filters";
import { mergeNewEvent } from "./helpers";

type FocusedEventPayload =
  | {
      event_uuid: string;
      gameState?: IGameState | null;
      focusedPlayers?: {
        focusedHitter?: IPlayer | null;
        focusedPitcher?: IPlayer | null;
        focusedCatcher?: IPlayer | null;
      };
    }
  | Record<string, never>;

export const initialState = {
  isTracking: false,
  staleContributingEvents: [] as TContributingEvents,
  queueLength: 20,
  isViewingPreviousEvent: false,
  isTaggingInterruptedEvent: false,

  /**
   * The current filter key for the event queue. It will resolve to a function that will be used internally to change the behavior of the selectors.
   * @example
   * ```ts
   * setCurrentFilter("has_pitch_data") // Will filter the queue to only include events with pitch data
   *
   * const queue = selectEventQueue(state) // The function will internally use the currentFilter to filter the queue
   * const recentEvent = selectRecentEvent(state) // The function will internally use the currentFilter to filter the queue
   * ```
   * @default "all"
   */
  currentFilterKey: "all" as FilterKey,

  /**
   * All the events received from the socket
   */
  eventQueue: [] as IQueueEvent[],

  /**
   * The payload of the event currently being tagged
   */
  focusedEventPayload: {} as FocusedEventPayload,
};

const socketSlice = createSlice({
  name: "socket",
  initialState,
  reducers: {
    setStaleEvents: (state, action: PayloadAction<string[]>) => {
      state.staleContributingEvents = [
        ...action.payload,
        ...state.staleContributingEvents,
      ];
    },
    setTrackingState: (state, action: PayloadAction<boolean>) => {
      state.isTracking = action.payload;
    },
    addEventToQueue: (state, action: PayloadAction<IQueueEvent>) => {
      state.eventQueue = mergeNewEvent(action.payload, state.eventQueue);
    },
    addHistoricEventsToQueue: (state, action: PayloadAction<IQueueEvent[]>) => {
      const eventsToAdd: IQueueEvent[] = action.payload;
      state.eventQueue = eventsToAdd.reduce(
        (acc, event) => mergeNewEvent(event, acc),
        state.eventQueue,
      );
    },
    deleteEventFromQueue: (state, action: PayloadAction<string>) => {
      const newQueue = state.eventQueue.filter(
        (event) => event.event_uuid !== action.payload,
      );
      state.eventQueue = newQueue;
    },
    setIsViewingPreviousEvent: (state, action: PayloadAction<boolean>) => {
      state.isViewingPreviousEvent = action.payload;
    },
    setFocusedEventPayload: (
      state,
      action: PayloadAction<FocusedEventPayload>,
    ) => {
      state.focusedEventPayload = {
        ...action.payload,
      };
    },
    updateQueueEvent: (state, action: PayloadAction<IQueueEvent>) => {
      const { event_uuid } = action.payload;
      const newQueue = state.eventQueue;
      const eventIndex = state.eventQueue.findIndex(
        (event) => event.event_uuid === event_uuid,
      );
      if (eventIndex !== -1) {
        newQueue[eventIndex] = action.payload;
        state.eventQueue = newQueue;
      }
    },
    incrementQueueLength: (state) => {
      state.queueLength += 20;
    },
    resetQueueLength: (state) => {
      state.queueLength = 20;
    },
    resetEventQueue: (state) => {
      state.eventQueue = [];
      state.isViewingPreviousEvent = false;
    },
    setIsTaggingInterruptedEvent: (state, action: PayloadAction<boolean>) => {
      state.isTaggingInterruptedEvent = action.payload;
    },
    setCurrentFilter: (state, action: PayloadAction<FilterKey>) => {
      state.currentFilterKey = action.payload;
    },
    updateEmptyEvent: (
      state,
      action: PayloadAction<{ currentUuid: string; createdUuid: string }>,
    ) => {
      const { currentUuid, createdUuid } = action.payload;
      const newQueue = state.eventQueue.map((event) => {
        if (event.event_uuid === currentUuid) {
          return {
            ...event,
            event_uuid: createdUuid,
            contributing_events: [createdUuid],
          };
        }
        return event;
      });
      state.eventQueue = newQueue;
    },
  },
});

export const {
  addEventToQueue,
  deleteEventFromQueue,
  incrementQueueLength,
  addHistoricEventsToQueue,
  resetEventQueue,
  resetQueueLength,
  setFocusedEventPayload,
  setIsTaggingInterruptedEvent,
  setIsViewingPreviousEvent,
  setStaleEvents,
  setTrackingState,
  updateQueueEvent,
  setCurrentFilter,
  updateEmptyEvent,
} = socketSlice.actions;
export default socketSlice.reducer;
